import type { IUser } from '../../../shared/interfaces/user.iterface';
import { LocalStorageService } from '../../../shared/services/local-storage.service';
import useAuthStore from '../store/auth.store';
import { loginRequest, logoutRequest, msalInstance, state } from '../../../config/msalConfig';
import { cloneDeep, every, first, get, includes, set, size, some, split, toString } from 'lodash';
import { PublicClientApplication, type AccountInfo } from '@azure/msal-browser';
import { storeToRefs } from 'pinia';
import { PermissionService } from './permission.service';
import { APP_DEFAULT_ADMIN_USER } from '../../../config/app.config';
import { useB2CPKCE } from '@/shared/pkce/useB2CPKCE';
import { useRouter } from 'vue-router';
import useUIStore from '@/modules/layout/store/ui.store';
import { enableB2CNetworkDebugging } from './networkDebugger';

export class AuthService {
    private authStoreVar?: ReturnType<typeof useAuthStore>;
    private uiStoreVar?: ReturnType<typeof useUIStore>;
    private routerVar?: ReturnType<typeof useRouter>;
    constructor(
        private env = import.meta.env,
        private localStorage = new LocalStorageService(),
        private permissionServices = new PermissionService()
    ) {
        this.localStorage = new LocalStorageService();
        this.permissionServices = new PermissionService();
    }

    private extension_groups_key = 'extension_groups';
    private tokenRefreshTimer: number | null = null;
    private isRefreshing = false; // Flag to prevent concurrent refresh attempts
    private get authStore(): ReturnType<typeof useAuthStore> {
        return (this.authStoreVar ??= useAuthStore());
    }

    private get uiStore(): ReturnType<typeof useUIStore> {
        return (this.uiStoreVar ??= useUIStore());
    }

    private get router(): ReturnType<typeof useRouter> {
        return (this.routerVar ??= useRouter());
    }

    async initMSAL(): Promise<void> {
        // Resolve Vue composables synchronously before any await/async boundary.
        // handleRedirect() runs inside a Promise .then() callback where Vue's
        // currentInstance is null, so useRouter() / useAuthStore() / useUIStore()
        // called lazily there would return undefined and cause a redirect loop.
        this.routerVar ??= useRouter();
        this.authStoreVar ??= useAuthStore();
        this.uiStoreVar ??= useUIStore();

        this.extension_groups_key = (this.env.VITE_AZURE_B2C_GROUP_PROPERTY as string) || 'extension_groups';

        // Enable network debugging only in development or staging
        if (this.env.VITE_APP_MODE === 'development' || this.env.VITE_APP_MODE === 'staging') {
            enableB2CNetworkDebugging();
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (this.env.VITE_APP_MODE === 'development' && this.env.VITE_APP_DEPLOYMENT_MODE === 'local') {
            this.authStore.setAuthenticated(true);
            this.authStore.setMSALUser(APP_DEFAULT_ADMIN_USER);

            this.authStore.setAccessTokenClaims({
                roles: [this.env.VITE_MSAL_ADMIN_ROLE_NAME, this.env.VITE_MSAL_AGENT_ROLE_NAME],
                [this.extension_groups_key]: [this.env.VITE_MSAL_ADMIN_ROLE_NAME, this.env.VITE_MSAL_AGENT_ROLE_NAME],

                given_name: this.env.VITE_USER_GIVEN_NAME,
                family_name: this.env.VITE_USER_FAMILY_NAME,
                ver: this.env.VITE_TOKEN_VERSION,
                oid: this.env.VITE_TOKEN_OBJECT_ID,
                sub: this.env.VITE_TOKEN_SUB_ID,
            });

            return;
        }
        try {
            await this.initialize();
            this.handleRedirect();
        } catch (error: unknown) {
            if (error instanceof Error) {
                throw new Error(error.message);
            }
            throw new Error('Failed to initialize MSAL');
        }
    }

    async initialize(): Promise<void> {
        if (msalInstance === null) {
            return;
        }
        try {
            await msalInstance.initialize();
        } catch (error: unknown) {
            if (error instanceof Error) {
                throw new Error(error.message);
            }
            throw new Error('Initialization error');
        }
    }

    async loginMSAL(): Promise<void> {
        try {
            // Check if MSAL is initialized before using it
            if (!msalInstance) {
                throw new Error('MSAL not initialized. Call initializeMsal() before using MSAL API.');
            }
            await msalInstance.loginRedirect(loginRequest);
            state.isAuthenticated = true;
            if (this.uiStore.nextUrl) {
                this.router.push({ path: this.uiStore.nextUrl });
                this.uiStore.setNextUrl(null);
                return;
            }
        } catch (error: unknown) {
            if (error instanceof Error) {
                throw new Error(error.message);
            }
            throw new Error('Login error');
        }
    }

    async loginPopupMSAL(): Promise<void> {
        try {
            // Check if MSAL is initialized before using it
            if (!msalInstance) {
                throw new Error('MSAL not initialized. Call initializeMsal() before using MSAL API.');
            }
            await msalInstance.loginPopup();
            state.isAuthenticated = true;
        } catch (error: unknown) {
            if (error instanceof Error) {
                throw new Error(error.message);
            }
            throw new Error('Login error');
        }
    }

    logoutMSAL(): void {
        // Clear token refresh timer
        this.clearTokenRefreshTimer();

        if (!msalInstance) {
            throw new Error('MSAL not initialized. Call initializeMsal() before using MSAL API.');
        }
        set(logoutRequest, 'account', msalInstance.getActiveAccount());
        set(logoutRequest, 'idTokenHint', this.authStore.idp_access_token);

        state.isAuthenticated = false;
        state.user = null;
        this.localStorage.removeItem('token');
        this.localStorage.removeItem('accessToken');
        this.authStore.resetUser();
        this.authStore.resetMSALUser();
        this.authStore.resetAuthenticated();
        this.authStore.resetToken();
        this.authStore.resetIpdAccessToken();
        this.authStore.resetAccessTokenClaims();
        msalInstance.logoutRedirect(logoutRequest);
    }

    startTokenRefreshTimer(msalInstance: PublicClientApplication): void {
        // Clear any existing timer
        if (this.tokenRefreshTimer !== null) {
            clearInterval(this.tokenRefreshTimer);
            this.tokenRefreshTimer = null;
        }

        const accounts = msalInstance.getAllAccounts();
        if (!accounts.length) return;

        const refreshInterval = 2 * 60 * 1000; // Check every 2 minutes
        const refreshThreshold = 10 * 60; // Refresh when < 10 minutes remaining

        let consecutiveFailures = 0;
        const maxFailures = 2;
        const isDevelopment = this.env.VITE_APP_MODE === 'development' || this.env.VITE_APP_MODE === 'staging';

        this.tokenRefreshTimer = setInterval(async () => {
            // Prevent concurrent refresh attempts from multiple timer instances
            if (this.isRefreshing) {
                // eslint-disable-next-line no-console
                if (isDevelopment) console.log('[Token Refresh] Already refreshing, skipping this interval');
                return;
            }

            try {
                this.isRefreshing = true;

                // Get current accounts
                const currentAccounts = msalInstance.getAllAccounts();
                if (!currentAccounts.length) {
                    // eslint-disable-next-line no-console
                    if (isDevelopment) console.warn('[Token Refresh] No accounts found — redirecting to login');
                    this.clearTokenRefreshTimer();
                    await msalInstance.loginRedirect(loginRequest);
                    return;
                }

                const account = first(currentAccounts) as AccountInfo;

                // eslint-disable-next-line no-console
                if (isDevelopment) console.log('[Token Refresh] Starting token check...');

                // Try to get token silently (will use cached token if available)
                let token;
                try {
                    token = await msalInstance.acquireTokenSilent({
                        scopes: loginRequest.scopes,
                        account: account,
                        forceRefresh: false,
                    });

                    // eslint-disable-next-line no-console
                    if (isDevelopment) console.log('[Token Refresh] Retrieved token from cache');
                } catch (cacheError) {
                    /* eslint-disable no-console */
                    if (isDevelopment) console.error('[Token Refresh] Failed to get cached token:', cacheError);

                    if (cacheError instanceof Error && cacheError.message.includes('failed_to_parse_response')) {
                        if (isDevelopment) console.error('[Token Refresh] Azure B2C returned HTML error page');
                        consecutiveFailures++;

                        if (consecutiveFailures >= maxFailures) {
                            if (isDevelopment)
                                console.error('[Token Refresh] Max failures reached - redirecting to login');
                            this.clearTokenRefreshTimer();
                            await msalInstance.loginRedirect(loginRequest);
                        }
                        return;
                    }

                    if (isDevelopment) console.warn('[Token Refresh] Redirecting to login due to cache error');
                    /* eslint-enable no-console */
                    this.clearTokenRefreshTimer();
                    await msalInstance.loginRedirect(loginRequest);
                    return;
                }

                // Check token expiration
                const expirationTimestamp = Math.floor((token.idTokenClaims as { exp: number }).exp);
                const currentTimestamp = Math.floor(Date.now() / 1000);
                const expiresIn = expirationTimestamp - currentTimestamp;

                if (isDevelopment) {
                    // eslint-disable-next-line no-console
                    console.log(
                        `[Token Refresh] Token expires in ${expiresIn} seconds (${Math.floor(expiresIn / 60)} minutes)`
                    );
                }

                // Only refresh if token is getting close to expiration
                if (expiresIn < refreshThreshold) {
                    if (isDevelopment)
                        // eslint-disable-next-line no-console
                        console.log('[Token Refresh] Token approaching expiration, attempting refresh...');

                    try {
                        const newToken = await msalInstance.acquireTokenSilent({
                            scopes: loginRequest.scopes,
                            forceRefresh: true,
                            account: account,
                        });

                        // eslint-disable-next-line no-console
                        if (isDevelopment) console.log('[Token Refresh] Token refreshed successfully');

                        // Update token in store and localStorage
                        this.authStore.setToken(toString(get(newToken, 'idToken')));
                        this.authStore.setIpdAccessToken(toString(get(newToken, 'idToken')));
                        this.authStore.setAccessTokenClaims(get(newToken, 'idTokenClaims'));
                        this.localStorage.setItem('token', toString(get(newToken, 'idToken')));
                        this.localStorage.setItem('accessToken', toString(get(newToken, 'idToken')));

                        const { selectAccessTokenClaims, selectAccessTokenRoles } = storeToRefs(this.authStore);
                        if (size(selectAccessTokenRoles.value) === 0) {
                            set(selectAccessTokenClaims.value, this.extension_groups_key, [
                                this.env.VITE_MSAL_GUEST_ROLE_NAME,
                            ]);
                        }

                        // Reset failure counter on success
                        consecutiveFailures = 0;
                    } catch (refreshError: unknown) {
                        consecutiveFailures++;

                        /* eslint-disable no-console */
                        if (isDevelopment) {
                            console.error(
                                `[Token Refresh] Refresh failed (attempt ${consecutiveFailures}/${maxFailures})`
                            );
                            console.error('[Token Refresh] Error:', refreshError);
                        }

                        // Check if it's the HTML response error
                        if (
                            refreshError instanceof Error &&
                            refreshError.message.includes('failed_to_parse_response')
                        ) {
                            if (isDevelopment)
                                console.error(
                                    '[Token Refresh] Azure B2C returned HTML error - refresh token may be invalid'
                                );

                            if (consecutiveFailures >= maxFailures) {
                                if (isDevelopment)
                                    console.error('[Token Refresh] Max failures reached - redirecting to login');
                                /* eslint-enable no-console */
                                this.clearTokenRefreshTimer();
                                await msalInstance.loginRedirect(loginRequest);
                            }
                        } else {
                            // eslint-disable-next-line no-console
                            if (isDevelopment) console.error('[Token Refresh] Unexpected error - redirecting to login');
                            this.clearTokenRefreshTimer();
                            await msalInstance.loginRedirect(loginRequest);
                        }
                    }
                } else {
                    // eslint-disable-next-line no-console
                    if (isDevelopment) console.log('[Token Refresh] Token still valid, no refresh needed');
                    consecutiveFailures = 0;
                }
            } catch (error: unknown) {
                // eslint-disable-next-line no-console
                if (isDevelopment) console.error('[Token Refresh] Unexpected error in token refresh timer:', error);
            } finally {
                // Always reset the flag
                this.isRefreshing = false;
            }
        }, refreshInterval);
    }

    private clearTokenRefreshTimer(): void {
        if (this.tokenRefreshTimer !== null) {
            clearInterval(this.tokenRefreshTimer);
            this.tokenRefreshTimer = null;
        }
        this.isRefreshing = false;
    }

    handleRedirect = (): void => {
        if (msalInstance === null) {
            return;
        }

        msalInstance
            .handleRedirectPromise()
            .then(() => {
                if (msalInstance === null) {
                    return;
                }

                const user: AccountInfo | null = cloneDeep(first(msalInstance.getAllAccounts()) as AccountInfo) ?? null;

                state.isAuthenticated = user !== null;
                state.user = user;
                if (!first(msalInstance.getAllAccounts())) {
                    this.router.push({
                        name: 'Home',
                    });
                    msalInstance.loginRedirect(loginRequest);
                    return;
                }
                if (!state.isAuthenticated) {
                    return;
                }

                msalInstance.setActiveAccount(user);

                this.authStore.setToken(toString(get(user, 'idToken')));
                this.authStore.setIpdAccessToken(toString(get(user, 'idToken')));
                this.authStore.setAccessTokenClaims(get(user, 'idTokenClaims'));
                this.localStorage.setItem('token', toString(get(user, 'idToken')));
                this.localStorage.setItem('accessToken', toString(get(user, 'idToken')));

                const { selectAccessTokenClaims, selectAccessTokenRoles } = storeToRefs(this.authStore);
                if (state?.user) {
                    this.authStore.setAuthenticated(state.isAuthenticated);
                    this.authStore.setMSALUser(state.user);
                }

                // Redirect to saved deep-link URL after login (survives MSAL page reload)
                const savedUrl = sessionStorage.getItem('postLoginRedirect');
                if (savedUrl) {
                    sessionStorage.removeItem('postLoginRedirect');
                    this.router.push(savedUrl);
                }

                if (size(selectAccessTokenRoles.value) === 0) {
                    set(selectAccessTokenClaims.value, this.extension_groups_key, [this.env.VITE_MSAL_GUEST_ROLE_NAME]);
                }

                if (
                    size(selectAccessTokenRoles.value) &&
                    every(
                        selectAccessTokenRoles.value,
                        (role) =>
                            !includes([this.env.VITE_MSAL_ADMIN_ROLE_NAME, this.env.VITE_MSAL_READER_ROLE_NAME], role)
                    )
                ) {
                    set(selectAccessTokenClaims.value, this.extension_groups_key, [this.env.VITE_MSAL_GUEST_ROLE_NAME]);
                }

                this.startTokenRefreshTimer(msalInstance);
            })
            .catch(async (error) => {
                if (error instanceof Error && error.message.includes('AADB2C90118')) {
                    const { redirectToB2C } = useB2CPKCE();
                    try {
                        await redirectToB2C({
                            tenant: first(
                                split(first(split(import.meta.env.VITE_MSAL_KNOW_AUTHORITIES, ',')), '.')
                            ) as string,
                            policy: import.meta.env.VITE_MSAL_PASSWORDRESET_ID,
                            clientId: import.meta.env.VITE_MSAL_CLIENT_ID,
                            redirectUri: import.meta.env.VITE_MSAL_REDIRECT_URI || window.location.origin,
                        });
                        return;
                    } catch (error) {
                        // eslint-disable-next-line no-console
                        console.debug('MSAL Error', error);
                        this.logoutMSAL();
                        return;
                    }
                }
                this.logoutMSAL();
            });
    };

    async login(request: { email: string; password: string }): Promise<void> {
        const currentUser: IUser = {
            email: request.email,
            password: request.password,
            firstName: 'John',
            lastName: 'Doe',
            permissions: {
                permissions: [this.env.VITE_MSAL_ADMIN_ROLE_NAME],
            },
            roles: { role: [this.env.VITE_MSAL_ADMIN_ROLE_NAME] },
        };
        this.authStore.setUser(currentUser);
        this.authStore.setAuthenticated(true);
        this.authStore.setToken('#token');
        this.localStorage.setItem('token', '#token');
    }

    logout(): void {
        this.localStorage.removeItem('token');
        this.localStorage.removeItem('accessToken');
        this.authStore.resetUser();
        this.authStore.resetAuthenticated();
        this.authStore.resetToken();
        this.authStore.resetIpdAccessToken();
        this.logoutMSAL();
    }

    hasPermissionByAction(action: string): boolean {
        const { selectAccessTokenRoles } = storeToRefs(this.authStore);
        const userRoles: string[] = selectAccessTokenRoles.value;
        if (!size(userRoles)) {
            return false;
        }
        const actionAllowedRoles: string[] = this.permissionServices.getPermissionRoles(action);
        const hasAccess: boolean = some(userRoles, (role) => includes(actionAllowedRoles, role));
        return hasAccess;
    }

    hasPermissionByRoles(roles: string[]): boolean {
        const { selectAccessTokenRoles } = storeToRefs(this.authStore);
        const userRoles: string[] = selectAccessTokenRoles.value;
        if (!size(userRoles)) {
            return false;
        }
        const hasAccess: boolean = some(userRoles, (role) => includes(roles, role));
        return hasAccess;
    }
}
